//@author: travis

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewEncapsulation,
} from "@angular/core";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { OrganizationService } from "../../../services/organization.service";
import { jSith } from "../../../util/jquery-replacement";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25BulkMap } from "../s25.bulk.edit.util";
import { S25BulkEditSaveApi } from "../save/s25.bulk.edit.save.api";
import { OrganizationMicroService } from "../../../services/organization.micro.service";
import { S25WsMicroOrganization } from "../../../services/MicroServices/S25WsMicroOrganization";
import { S25Const } from "../../../util/s25-const";
import ItemContact = S25WsMicroOrganization.ItemContact;
import { ContactRole, UpdatedContactRoles, ContactRoles, ContactRoleId } from "../../../services/organization.service";

@TypeManagerDecorator("s25-ng-bulk-edit-org-contacts")
@Component({
    selector: "s25-ng-bulk-edit-org-contacts",
    template: `@if (this.init) {
        <div>
            <span class="c-margin-top--single c-margin-bottom--half c-margin-right--half ngInlineBlock ngBold"
                >Associated Contacts</span
            >
            <s25-toggle-button
                (valueChange)="this.onToggle($event)"
                [value]="this.remove"
                [trueLabel]="'Remove'"
                [falseLabel]="'Add'"
            ></s25-toggle-button>
            <s25-ng-contacts-editable
                [(modelBean)]="this.orgContacts"
                [itemTypeId]="itemTypeId"
                [usersOnly]="false"
                [primaryContact]="!this.remove"
                (modelBeanChange)="onModelBeanChange($event)"
            ></s25-ng-contacts-editable>
            <s25-ng-bulk-edit-save
                [submitFn]="updateFn"
                [buttonText]="this.saveButtonText"
                [submitFn]="update"
            ></s25-ng-bulk-edit-save>
        </div>
    }`,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25BulkEditOrgContactsComponent implements OnInit {
    @Input() itemIds: number[];
    @Input() itemTypeId?: number = 3;
    init: boolean = false;
    orgContacts: S25ItemI[];
    remove: boolean = false;
    saveButtonText = "Add Associated Contacts";
    mappingModelBean: any;

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {
        this.elementRef.nativeElement.angBridge = this; //bridge to AngularJS; used for AngJS to set model values and call setter fns
    }

    ngOnInit(): void {
        this.mappingModelBean = S25BulkMap[this.itemTypeId].contact;
        this.orgContacts = [];
        this.init = true;
        this.cd.detectChanges();
    }

    get updateFn() {
        return this.update.bind(this);
    }

    update() {
        if (this.orgContacts && this.orgContacts.length < 1) {
            return jSith.reject("Please select a contact");
        }
        const primaryContact = !this.remove && S25Util.array.getByProp(this.orgContacts, "isPrimary", true);

        // Transform contacts array to match micro service format
        const contacts: ItemContact[] = this.orgContacts
            .flatMap((c) => {
                // If property is an array, create an entry for each role
                if (Array.isArray(c.property) && c.property.length > 0) {
                    return c.property.map((role) => ({
                        contactId: Number(c.itemId),
                        contactRoleId: Number(role.itemId) || S25Const.unassignedRole.organization,
                    }));
                }
                // If no roles specified, use unassigned role
                return [
                    {
                        contactId: Number(c.itemId),
                        contactRoleId: S25Const.unassignedRole.organization,
                    },
                ];
            })
            .filter(Boolean) as ItemContact[]; // Remove any undefined entries

        const payload: Partial<S25WsMicroOrganization.Item> = {
            idList: this.itemIds,
        };

        // Only include primeContact if it exists and we're not removing
        if (!this.remove && primaryContact?.itemId) {
            payload.primeContact = Number(primaryContact.itemId);
        }

        if (this.remove) {
            // For removal, put contacts in the remove.contacts array
            payload.remove = {
                contacts: contacts,
            };
        } else {
            // For addition, put contacts in the contacts array
            payload.contacts = contacts;

            // If adding contacts with other roles, also remove role_id=-2 / S25Const.unassignedRole.organization for those contacts
            const contactsWithOtherRoles = contacts.filter(
                (c) => c.contactRoleId !== S25Const.unassignedRole.organization,
            );
            if (contactsWithOtherRoles.length > 0) {
                // Get unique contact IDs that have other roles
                const contactIdsToRemoveNoRole = [...new Set(contactsWithOtherRoles.map((c) => c.contactId))];
                const removeNoRole: ItemContact[] = contactIdsToRemoveNoRole.map((contactId) => ({
                    contactId: Number(contactId),
                    contactRoleId: S25Const.unassignedRole.organization,
                }));

                payload.remove = {
                    contacts: removeNoRole,
                };
            }
        }
        return OrganizationMicroService.saveOrganization(payload);
    }

    onToggle(event: any) {
        this.remove = event;

        const removeButtonText = "Remove " + this.mappingModelBean.title;
        const addButtonText = "Add " + this.mappingModelBean.title;

        this.saveButtonText = this.remove ? removeButtonText : addButtonText;
        this.cd.detectChanges();
    }

    onModelBeanChange() {
        S25BulkEditSaveApi.reset(this.elementRef.nativeElement);
    }
}
